import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DataTable,
  HairlineCard,
  Icons,
  iconSize,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { useScope } from '../providers/ScopeProvider';
import { formatDate } from '../lib/relative-time';
import { milestoneLabelKey, usePublicFile, usePublicFiles } from '../lib/public-view-api';

/**
 * Every file at the municipality, as the public sees it.
 *
 * What a municipality without its own Government Portal licence gets in place
 * of the casework screens: all of its files, each shown only as far as a
 * property owner sees theirs on the Client Portal — the reference, the civic
 * address and the dated public milestones. The case file itself stays with the
 * contractor's officers.
 */
export function PublicFiles() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { active } = useScope();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : undefined;
  const files = usePublicFiles(localGovernmentId);
  const actionsColumn = useRowActionsColumn();

  return (
    <AppShell title={t('gov.publicView.title')}>
      <PageIntro>{t('gov.publicView.intro')}</PageIntro>
      {!localGovernmentId ? (
        <Alert severity="info">{t('gov.publicView.pickMunicipality')}</Alert>
      ) : (
        <DataTable
          rows={files.data ?? []}
          getRowKey={(row) => row.id}
          isLoading={files.isLoading}
          emptyLabel={t('gov.publicView.empty')}
          pageSize={25}
          initialSortKey="updated"
          initialSortDir="desc"
          rowHref={(row) => `/public-files/${row.id}`}
          linkComponent={RouterLink}
          search={{
            placeholder: t('gov.publicView.search'),
            of: (row) => `${row.reference} ${row.address}`,
          }}
          columns={[
            {
              key: 'reference',
              label: t('gov.publicView.reference'),
              primary: true,
              sortValue: (row) => row.reference,
              cell: (row) => row.reference,
            },
            {
              key: 'address',
              label: t('gov.publicView.address'),
              sortValue: (row) => row.address,
              cell: (row) => row.address,
            },
            {
              key: 'latest',
              label: t('gov.publicView.latest'),
              sortValue: (row) => row.latestMilestone ?? '',
              cell: (row) =>
                row.latestMilestone ? t(milestoneLabelKey(row.latestMilestone)) : '—',
            },
            {
              key: 'updated',
              label: t('gov.publicView.updated'),
              nowrap: true,
              sortValue: (row) => row.lastUpdatedOn ?? '',
              cell: (row) => formatDate(row.lastUpdatedOn, i18n.language),
            },
            {
              key: 'deadline',
              label: t('gov.publicView.deadline'),
              nowrap: true,
              sortValue: (row) => row.nextDeadline ?? '',
              cell: (row) =>
                row.nextDeadline ? (
                  <StatusChip
                    status="pending"
                    label={formatDate(row.nextDeadline, i18n.language)}
                  />
                ) : (
                  '—'
                ),
            },
            {
              ...actionsColumn,
              cell: (row) => (
                <RowActions>
                  <RowAction
                    label={t('gov.publicView.open')}
                    icon={Icons.ChevronRight}
                    onClick={() => navigate(`/public-files/${row.id}`)}
                  />
                </RowActions>
              ),
            },
          ]}
        />
      )}
    </AppShell>
  );
}

/** One file's public timeline — the same milestones the owner sees. */
export function PublicFileDetail() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { active } = useScope();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : undefined;
  const file = usePublicFile(localGovernmentId, id);

  return (
    <AppShell
      title={file.data?.reference ?? t('gov.publicView.title')}
      actions={
        <Button
          startIcon={<Icons.ArrowLeft size={iconSize.md} aria-hidden />}
          onClick={() => navigate('/public-files')}
        >
          {t('gov.publicView.back')}
        </Button>
      }
    >
      {file.isLoading ? (
        <CircularProgress />
      ) : !file.data ? (
        <Alert severity="warning">{t('gov.publicView.notFound')}</Alert>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body1" color="text.secondary">
            {file.data.address}
          </Typography>
          <HairlineCard sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {t('gov.publicView.timeline')}
            </Typography>
            <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {file.data.milestones.map((m) => (
                <Box
                  component="li"
                  key={m.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 1.25,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="body2">{t(milestoneLabelKey(m.type))}</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  >
                    {formatDate(m.occurredOn, i18n.language)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </HairlineCard>
          <Typography variant="caption" color="text.secondary">
            {t('gov.publicView.limitedNote')}
          </Typography>
        </Stack>
      )}
    </AppShell>
  );
}
